<template>
  <div class="rm-drawer-step">
    <div class="rm-drawer-scroll">
      <h3 class="drawer-section-title mb-4">{{ $t('ESTIMATE_REPAIRER_OFFER.SUB_TITLE') }}</h3>

      <div class="repairer-offer-list mb-4">
        <template
          v-for="option of currentRepairFile?.repairer_estimate?.repairer_estimate_option_list"
          :key="option.technical_id"
        >
          <v-card-secondary class="repairer-offer-card">
            <div class="drawer-form-header repairer-offer-card-header">
              <v-chip class="repairer-offer-chip" color="primary" size="small" variant="tonal">
                {{ $t('COMMON.PRODUCT_END_STATUS.' + option.offer_type.toUpperCase()) }}
              </v-chip>
              <div v-if="!activeEditOption" class="repairer-offer-actions">
                <v-btn-icon
                  :aria-label="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_PRICE')"
                  :title="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_PRICE')"
                  icon="mdi-pencil-outline"
                  size="small"
                  @click="startEdit(option, 'totalCost')"
                />
                <v-btn-icon
                  :aria-label="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_MARGIN')"
                  :title="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_MARGIN')"
                  icon="mdi-percent-outline"
                  size="small"
                  @click="startEdit(option, 'margin')"
                />
                <v-btn-icon
                  :aria-label="$t('COMMON.BUTTON.DELETE')"
                  :title="$t('COMMON.BUTTON.DELETE')"
                  color="error"
                  icon="mdi-trash-can-outline"
                  size="small"
                  @click="deleteRepairerEstimateOption(option)"
                />
              </div>
            </div>

            <div class="detail-info-row repairer-offer-comment-row">
              <span class="detail-info-label">
                {{ $t('ESTIMATE_REPAIRER_OFFER.DETAIL_CLIENT') }}
              </span>
              <strong class="detail-info-value">{{ $displayValue(option.comment) }}</strong>
            </div>

            <div class="repairer-offer-metrics">
              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{ $t('ESTIMATE_REPAIRER_OFFER.CLIENT_PRICE') }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{ Number(option.total_cost).toFixed(2) }} {{ $t('COMMON.EURO') }}
                </p>
              </div>
              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{
                    $t(
                      isReplacementEstimateOffer(option)
                        ? 'COMMON.COST'
                        : 'ESTIMATE_REPAIRER_OFFER.REPAIRER_COST_SHORT'
                    )
                  }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{ repairerCost(option).toFixed(2) }} {{ $t('COMMON.EURO') }}
                </p>
              </div>
              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{ $t('ESTIMATE_REPAIRER_OFFER.MARGIN') }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{ option.margin }} {{ $t('COMMON.PERCENT') }}
                </p>
              </div>
            </div>
          </v-card-secondary>
          <v-form-card
            v-if="activeEditOption?.technical_id === option.technical_id"
            ref="formUpdateEstimateRepairerOption"
            v-model="editFormValid"
            @submit.prevent="submitEditForm"
          >
            <div class="drawer-form-header">
              <p class="drawer-form-title">{{ activeEditTitle }}</p>
              <v-btn-icon
                class="drawer-form-close"
                :aria-label="$t('COMMON.BUTTON.CLOSE')"
                :title="$t('COMMON.BUTTON.CLOSE')"
                icon="mdi-close"
                size="small"
                @click="closeEditForm"
              />
            </div>
            <div class="drawer-form-field">
              <p class="custom-label" required="true">{{ activeEditLabel }}</p>
              <v-text-field
                v-model="activeEditValue"
                type="number"
                :rules="activeEditType === 'margin' ? marginRules : numberRules"
                hide-details="auto"
              >
                <template #append-inner>
                  <span class="drawer-field-suffix">
                    {{ activeEditType === 'margin' ? $t('COMMON.PERCENT') : $t('COMMON.EURO') }}
                  </span>
                </template>
              </v-text-field>
            </div>
            <v-btn-secondary
              class="w-100"
              :disabled="!editFormValid || loading"
              :loading="loading"
              prepend-icon="mdi-check"
              variant="tonal"
              type="submit"
            >
              {{ activeEditSaveLabel }}
            </v-btn-secondary>
          </v-form-card>
        </template>
      </div>

      <button
        v-if="repairFileEndStatusList.length && !showRepairerOptionForm && !activeEditOption"
        class="repairer-offer-add-card"
        type="button"
        @click="showRepairerOptionForm = true"
      >
        <span class="repairer-offer-add-icon"><v-icon icon="mdi-plus" size="18" /></span>
        <span>{{ $t('ESTIMATE_REPAIRER_OFFER.BUTTON.ADD_FORM') }}</span>
      </button>

      <v-form-card
        v-if="showRepairerOptionForm && !activeEditOption"
        ref="formEstimateRepairerOption"
        v-model="optionFormValid"
        @submit.prevent="addEstimateRepairerOption"
      >
        <div class="drawer-form-header">
          <p class="drawer-form-title">{{ $t('ESTIMATE_REPAIRER_OFFER.NEW_OFFER') }}</p>
          <v-btn-icon
            class="drawer-form-close"
            :aria-label="$t('COMMON.BUTTON.CLOSE')"
            :title="$t('COMMON.BUTTON.CLOSE')"
            icon="mdi-close"
            size="small"
            @click="showRepairerOptionForm = false"
          />
        </div>
        <div class="drawer-form-field">
          <p class="custom-label" required="true">
            {{ $t('ESTIMATE_REPAIRER_OFFER.OFFER_TYPE') }}
          </p>
          <custom-autocomplete
            id="select"
            v-model="repairFileEndStatusSelected"
            :items="repairFileEndStatusList"
            :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.OFFER_TYPE')"
            item-title="name"
            item-value="technical_id"
            required
            :rules="requiredRules"
            hide-details="auto"
          />
        </div>
        <div class="drawer-form-field">
          <p class="custom-label" required="true">
            {{ $t('ESTIMATE_REPAIRER_OFFER.DETAIL_CLIENT') }}
          </p>
          <v-textarea
            v-model.trim="comment"
            :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.COMMENT')"
            :rules="requiredRules"
            auto-grow
            rows="2"
            :hide-details="!commentIsFocused"
            :maxlength="500"
            :counter="commentIsFocused"
            @update:focused="commentIsFocused = $event"
          />
        </div>
        <div class="repairer-offer-form-grid">
          <div class="drawer-form-field">
            <p class="custom-label" required="true">
              {{
                $t(
                  isReplacementEstimateOffer({ offer_type: repairFileEndStatusSelected })
                    ? 'COMMON.COST_EXCLUSIVE_TAX'
                    : 'ESTIMATE_REPAIRER_OFFER.REPAIR_COST_HT'
                )
              }}
            </p>
            <v-text-field
              v-model="estimateCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.ESTIMATE_COST')"
              type="number"
              :rules="[...requiredRules, ...numberRules]"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>
          <div class="drawer-form-field">
            <p class="custom-label">{{ $t('ESTIMATE_REPAIRER_OFFER.TRANSPORT_HT') }}</p>
            <v-text-field
              v-model="transportCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.TRANSPORT_COST')"
              type="number"
              :rules="numberRules"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>
          <div v-if="repairFileEndStatusSelected === 'PRODUCT_REPAIRED'" class="drawer-form-field">
            <p class="custom-label">{{ $t('ESTIMATE_REPAIRER_OFFER.LABOR_HT') }}</p>
            <v-text-field
              v-model="laborCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.LABOR_COST')"
              type="number"
              :rules="numberRules"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>
        </div>
        <v-btn-secondary
          class="w-100"
          :disabled="!optionFormValid || loading || !repairFileEndStatusSelected"
          :loading="loading"
          prepend-icon="mdi-plus"
          variant="tonal"
          type="submit"
        >
          {{ $t('ESTIMATE_REPAIRER_OFFER.BUTTON.VALIDATE_OFFER') }}
        </v-btn-secondary>
      </v-form-card>

      <p class="repairer-offer-info">{{ $t('ESTIMATE_REPAIRER_OFFER.INFO_CLIENT') }}</p>
    </div>
  </div>
</template>

<script>
import RepairFileService from '@/services/RepairFileService'
import RepairFileEndStatusConstant from '@/constants/repair-module/repair-file/repairFileEndStatusConstant'
import Validators from '@/utils/validators'
import { isReplacementEstimateOffer } from '@/utils/repairFileUtils'
import { handleResponseError } from '@/utils/toastUtils'

export default {
  props: { currentRepairFile: Object },
  data() {
    return {
      loading: false,
      optionFormValid: false,
      editFormValid: false,
      showRepairerOptionForm: false,
      repairFileEndStatusList: [],
      repairFileEndStatusSelected: null,
      comment: null,
      commentIsFocused: false,
      estimateCost: null,
      transportCost: null,
      laborCost: null,
      totalCost: null,
      baseMargin: 25,
      activeEditOption: null,
      activeEditType: null,
      activeEditValue: null,
      requiredRules: Validators.requiredRules,
      numberRules: Validators.numberRules,
      marginRules: Validators.marginRules
    }
  },
  computed: {
    footerState() {
      return {
        disabled:
          this.loading ||
          !this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list?.length,
        loading: this.loading
      }
    },
    activeEditTitle() {
      return this.$t(
        this.activeEditType === 'margin'
          ? 'ESTIMATE_REPAIRER_OFFER.MODIFICATION_MARGIN'
          : 'ESTIMATE_REPAIRER_OFFER.MODIFICATION_PRICE'
      )
    },
    activeEditLabel() {
      return this.$t(
        this.activeEditType === 'margin'
          ? 'ESTIMATE_REPAIRER_OFFER.MARGIN'
          : 'ESTIMATE_REPAIRER_OFFER.CLIENT_PRICE_HT'
      )
    },
    activeEditSaveLabel() {
      return this.$t(
        this.activeEditType === 'margin'
          ? 'ESTIMATE_REPAIRER_OFFER.SAVE_MARGIN'
          : 'ESTIMATE_REPAIRER_OFFER.SAVE_PRICE'
      )
    }
  },
  created() {
    this.patchRepairFileEndStatusList()
  },
  mounted() {
    this.emitFooterState()
  },
  watch: {
    footerState: {
      handler() {
        this.emitFooterState()
      },
      deep: true
    },
    currentRepairFile() {
      this.patchRepairFileEndStatusList()
    },
    showRepairerOptionForm() {
      this.patchRepairFileEndStatusList()
    }
  },
  methods: {
    isReplacementEstimateOffer,
    emitFooterState() {
      this.$emit('updateFooterState', this.footerState)
    },
    repairerCost(option) {
      return option.estimate_cost + option.transport_cost + option.labor_cost
    },
    startEdit(option, type) {
      this.activeEditOption = option
      this.activeEditType = type
      this.activeEditValue = Number(type === 'margin' ? option.margin : option.total_cost)
      this.showRepairerOptionForm = false
      this.editFormValid = false
    },
    closeEditForm() {
      this.activeEditOption = null
      this.activeEditType = null
      this.activeEditValue = null
      this.editFormValid = false
    },
    submitEditForm() {
      if (!this.activeEditOption || !this.editFormValid) return
      if (this.activeEditType === 'margin') {
        this.activeEditOption.margin = Number(this.activeEditValue)
        this.calculateNewTotalCost(this.activeEditOption)
      } else {
        this.activeEditOption.total_cost = Number(this.activeEditValue)
        this.calculateNewMargin(this.activeEditOption)
      }
      this.updateRepairerEstimateOption(this.activeEditOption)
    },
    patchRepairFileEndStatusList() {
      this.repairFileEndStatusList = Object.keys(RepairFileEndStatusConstant)
        .map((key) => ({
          technical_id: key,
          name: this.$t(
            `REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PRODUCT_END_STATUS.${RepairFileEndStatusConstant[key]}`
          )
        }))
        .filter(
          (status) =>
            !this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.some(
              (option) => option.offer_type.toUpperCase() === status.technical_id
            )
        )
    },
    async addEstimateRepairerOption() {
      this.loading = true
      this.calculateBaseTotalCost()
      try {
        await RepairFileService.createRepairFileRepairerEstimateOption({
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_id: this.currentRepairFile.repairer_estimate.technical_id,
          offer_type: this.repairFileEndStatusSelected,
          comment: this.comment,
          margin: this.baseMargin,
          estimate_cost: this.estimateCost,
          transport_cost: this.transportCost || 0,
          labor_cost: this.laborCost || 0,
          total_cost: this.totalCost
        })
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.showRepairerOptionForm = false
        this.repairFileEndStatusSelected = null
        this.comment = null
        this.estimateCost = null
        this.transportCost = null
        this.laborCost = null
        this.totalCost = null
        this.patchRepairFileEndStatusList()
      }
    },
    async updateRepairerEstimateOption(option) {
      this.loading = true
      try {
        await RepairFileService.updateRepairFileRepairerEstimateOption({
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_id: this.currentRepairFile.repairer_estimate.technical_id,
          repairer_estimate_option_id: option.technical_id,
          offer_type: option.offer_type,
          comment: option.comment,
          margin: Number(option.margin),
          estimate_cost: option.estimate_cost,
          transport_cost: option.transport_cost || 0,
          labor_cost: option.labor_cost || 0,
          total_cost: Number(option.total_cost)
        })
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.closeEditForm()
      }
    },
    async deleteRepairerEstimateOption(option) {
      this.loading = true
      try {
        await RepairFileService.deleteRepairFileRepairerEstimateOption({
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_option_id: option.technical_id
        })
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async submit() {
      this.loading = true
      try {
        await RepairFileService.confirmationRepairFileRepairerEstimate({
          repair_file_id: this.currentRepairFile.technical_id
        })
        this.$emit('updateCurrentRepairFile')
        this.$emit('openEstimateRepairerDetail')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.patchRepairFileEndStatusList()
      }
    },
    calculateBaseTotalCost() {
      this.totalCost =
        (Number(this.estimateCost) + Number(this.transportCost) + Number(this.laborCost)) /
        (1 - this.baseMargin / 100)
    },
    calculateNewMargin(option) {
      option.margin = Number(
        ((100 * (option.total_cost - this.repairerCost(option))) / option.total_cost).toFixed(0)
      )
    },
    calculateNewTotalCost(option) {
      option.total_cost = this.repairerCost(option) / (1 - option.margin / 100)
    }
  }
}
</script>

<style scoped>
.repairer-offer-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.repairer-offer-card {
  padding: 16px;
}
.repairer-offer-card-header {
  justify-content: space-between;
}
.repairer-offer-chip {
  max-width: calc(100% - 112px);
}
.repairer-offer-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}
.repairer-offer-comment-row {
  margin: 8px 0 16px;
}
.repairer-offer-metrics,
.repairer-offer-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.repairer-offer-metrics {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.repairer-offer-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 96px;
  padding: 16px;
  border: 2px dashed #d8dee4;
  border-radius: 12px;
  background-color: var(--soft-surface-color);
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
}
.repairer-offer-add-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.55);
  background: rgba(var(--v-theme-primary), 0.04);
}
.repairer-offer-add-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}
.repairer-offer-info {
  margin: 12px 0 0;
  padding-inline: 14px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 13px;
  line-height: 1.4;
}
@media (max-width: 420px) {
  .repairer-offer-metrics,
  .repairer-offer-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
